import React, { useCallback, useMemo, useRef, useState } from "react";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import sanitizeHtml from "sanitize-html";

// import './UserFormatableText.css';
// Using custom stylesheet, because the theme enforces a style on the generated content
// But we want to use the existing `content` style from bulma.css
// import 'react-quill/dist/quill.snow.css';

const EMPTY_VALUE = "<p><br></p>";

const modules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [
      { list: "ordered" },
      { list: "bullet" } /*, {'indent': '-1'}, {'indent': '+1'} */,
    ],
    ["link"],
    ["clean"],
  ],
  // clipboard: {
  //   matchers: [["BR", lineBreakMatcher]],
  //   matchVisual: false
  // },
  // keyboard: {
  //   bindings: {
  //     linebreak: {
  //       key: 13,
  //       shiftKey: true,
  //       handler: function(range) {
  //         console.log('linebreak handler', range, this.quill);
  //         const currentLeaf = this.quill.getLeaf(range.index)[0];
  //         const nextLeaf = this.quill.getLeaf(range.index + 1)[0];

  //         const insertion = this.quill.editor.insertEmbed(range.index, 'break', true, "user");
  //         // const insertion = this.quill.editor.insertText(range.index, '\n', "user");
  //         console.log('after first break insert', this.quill.getContents(), insertion, window.qq = this.quill);

  //         // Insert a second break if:
  //         // At the end of the editor, OR next leaf has a different parent (<p>)
  //         if (nextLeaf === null || currentLeaf.parent !== nextLeaf.parent) {
  //           const insertion = this.quill.editor.insertEmbed(range.index, 'break', true, "user");
  //           // this.quill.editor.insertText(range.index, '\n', "user");
  //         }
  //         // Now that we've inserted a line break, move the cursor forward
  //         this.quill.setSelection(range.index + 1, Quill.sources.SILENT);

  //         console.log('end of handler', this.quill.getContents());
  //       }
  //     }
  //   }
  // }
};

const ALLOWED_TAGS = [
  "h1", // header 1
  "h2", // header 2
  "h3", // header 3
  "a", // anchor
  "strong", // bold
  "em", // italic
  "u", // underline
  "s", // strikethrough
  "blockquote", // quote
  "ol", // ordered list
  "li", // list item
  "ul", // unordered list
  "p", // paragraph
  "br", // line break
];

const UserFormatableTextDisplay = ({ className, style, value }) => {
  const router = useRouter();

  const contentClickHandler = useCallback(
    (e) => {
      const targetLink = e.target.closest("a");

      if (!targetLink) return;

      const siteUrl = window.location.origin;

      let link = null;
      try {
        link = new URL(targetLink.href);
      } catch {}

      if (siteUrl === link?.origin) {
        e.preventDefault();
        router.push(link.pathname + link.search);
      }
    },
    [router]
  );

  // handle legacy line breaks by replacing \n with <br
  const updatedValue = value.replaceAll("\n", "<br />");

  return (
    <div
      className={"content " + (className || "")}
      style={{ overflowWrap: "anywhere" /* for extra long lines  */, ...style }}
      onClick={contentClickHandler}
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(updatedValue) }}
    ></div>
  );
};

const UserFormatableTextOnlyDisplay = ({
  className,
  value,
  maxLines = 3,
  trimTill = undefined,
}) => {
  const strippedValue = useMemo(() => {
    return sanitizeHtml(value, {
      allowedTags: [],
      disallowedTagsMode: "discard",
    });
  }, [value]);

  return (
    <div
      className={className}
      style={{
        whiteSpace: "pre-line",
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "-webkit-box",
        WebkitLineClamp: maxLines,
        WebkitBoxOrient: "vertical",
      }}
    >
      {strippedValue.slice(0, trimTill)}
      {trimTill ? "..." : ""}
    </div>
  );
};

const EditorComponent = dynamic(() => import("./EditorComponent"), {
  ssr: false,
  loading: () => <div>Loading Editor...</div>,
});

const UserFormatableText = ({
  height = 250,
  id,
  value,
  onBlur,
  onChange,
  placeholder,
}) => {
  const [toolbarHeight, setToolbarHeight] = useState(43); // default
  const editorRef = useRef(null);

  const editorMount = (editorElement) => {
    if (!editorElement) return;
    setToolbarHeight(
      editorElement.editor.container.parentElement.querySelector(".ql-toolbar")
        .offsetHeight
    );
    editorRef.current = editorElement;
  };

  // handle legacy line breaks by replacing \n with <br>
  const updatedValue = value.replaceAll("\n", "<br />");

  return (
    <div style={{ height }}>
      <EditorComponent
        editorRef={editorMount}
        id={id}
        className="content" // for bulma.css of html content
        style={{ height: `calc(100% - ${toolbarHeight}px)` }} // because of weird styling glitch where the toolbar height is not considered
        theme="snow"
        modules={modules}
        value={updatedValue}
        onChange={(v, _, __, _editor) => onChange(v === EMPTY_VALUE ? "" : v)}
        onBlur={(_previousRange, _source, _editor) =>
          onBlur({
            target: editorRef.current?.editor.container.parentElement,
          })
        }
        placeholder={placeholder} // placeholder doesn't work because empty state is '\n' ...
      ></EditorComponent>
    </div>
  );
};

export default UserFormatableText;

export { UserFormatableTextDisplay, UserFormatableTextOnlyDisplay };
